import React from "react";
import moment from "moment";

import StatusBadge from "designV2/components/StatusBadge";
import { DetailField, CopyableField, ImageStrip, Stars, assetUrl, humanize } from "../shopFields";
import "../superShops.css";

const BUDGET_LABELS = { B: "$", BB: "$$", BBB: "$$$" };

/**
 * ShopVerificationDetails - content-only, rendered inside a DialogShell.
 *
 * The previous version drew its own header and close button, wired up two
 * react-image-lightbox instances for the logo and banner, and showed only
 * five fields — name, phone, root category, state and the owner's email.
 * Everything a person actually needs in order to decide whether to
 * approve a shop (the address, the owner's phone, the cuisines, when it
 * applied, whether it was rejected before and why) was not on screen.
 */
const ShopVerificationDetails = ({ item }) => {
  if (!item) return null;

  const logo = assetUrl(item.logoUrl);
  const banner = assetUrl(item.bannerUrl);
  const cuisines = (item.attributes || []).map((a) => a.name);
  const categories = (item.categories || []).map((c) => c.title);

  return (
    <div className="superShopDetails">
      <div className="superShopDetailsTop">
        <StatusBadge
          status={item.verified ? "success" : item.isRejected ? "danger" : "warning"}
          label={item.verified ? "Verified" : item.isRejected ? "Rejected" : "Awaiting review"}
        />
        <StatusBadge
          status={item.state === "SUSPENDED" ? "danger" : "neutral"}
          label={humanize(item.state) || "Unknown"}
          dot={false}
        />
        {item.rootCategory && <span className="superShopTag">{item.rootCategory.title}</span>}
        <span className="superShopDetailsHeadline">{item.name}</span>
      </div>

      {item.isRejected && item.rejectionMessage && (
        <div className="dsFormMessage warning">
          <span>
            <strong>Previously rejected:</strong> {item.rejectionMessage}
          </span>
        </div>
      )}

      <div className="superShopGrid">
        <CopyableField label="Shop ID" value={item._id} />
        <DetailField label="Rating" value={<Stars value={item.averageRate} />} />
        <DetailField
          label="Owner"
          value={
            item.shopAdmin ? (
              <React.Fragment>
                {item.shopAdmin.fullName}
                <br />
                <span className="superShopMuted">{item.shopAdmin.phoneNumber}</span>
                {item.shopAdmin.email && (
                  <React.Fragment>
                    <br />
                    <span className="superShopMuted">{item.shopAdmin.email}</span>
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : null
          }
        />
        <DetailField label="Shop phone" value={(item.phoneNumbers || []).join(", ") || null} />
        <DetailField label="Price range" value={BUDGET_LABELS[item.budget] || item.budget} />
        <DetailField label="Open now" value={item.active ? "Yes" : "No"} />
        <DetailField label="Categories" value={categories.join(", ") || null} />
        <DetailField label="Cuisines" value={cuisines.join(", ") || null} />
        <DetailField label="Address" value={item.address} wide />
        <DetailField
          label="Applied"
          value={item.createdAt ? moment(item.createdAt).format("MMM D, YYYY [at] hh:mm A") : null}
        />
        <DetailField label="Description" value={item.description} wide />
      </div>

      <ImageStrip label="Logo" urls={logo ? [logo] : []} />
      <ImageStrip label="Banner" urls={banner ? [banner] : []} />
    </div>
  );
};

export default ShopVerificationDetails;
